import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/solid';

// Reusable Form Components
const FormInput = ({ id, name, label, type, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type={type} id={id} name={name} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);
const TextArea = ({ id, name, label, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <textarea id={id} name={name} value={value} onChange={onChange} required={required} rows="4" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);
const FileInput = ({ id, name, label, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type="file" id={id} name={name} onChange={onChange} required={required} accept="image/png, image/jpeg, image/webp" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-semibold file:bg-orange-50 dark:file:bg-orange-900/50 file:text-orange-700 dark:file:text-orange-200 hover:file:bg-orange-100 dark:hover:file:bg-orange-900" />
    </div>
);

export default function ManageSuccessStoriesPage() {
    const { token } = useAuth();
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [formData, setFormData] = useState({ title: '', recipientName: '', storyText: '', image: null });

    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/stories/admin/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Could not fetch stories.');
            const data = await response.json();
            setStories(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({ ...prev, [name]: files ? files[0] : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.image) {
            setError('An image is required to create a story.');
            return;
        }
        setIsSubmitting(true);
        setMessage('');
        setError('');
        try {
            // Create FormData object for file upload
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('recipientName', formData.recipientName);
            formDataToSend.append('storyText', formData.storyText);
            formDataToSend.append('image', formData.image);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/stories/admin`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Don't set Content-Type - let the browser set it with boundary for FormData
                },
                body: formDataToSend
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Failed to create story.');
            
            setMessage(`Successfully created story: "${data.title}"`);
            setFormData({ title: '', recipientName: '', storyText: '', image: null });
            e.target.reset();
            fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleDelete = async (storyId) => {
        if (!window.confirm('Are you sure you want to permanently delete this success story?')) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/stories/admin/${storyId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            setMessage(data.message);
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <p className="p-8 text-center">Loading Success Stories...</p>;

    return (
        <div className="space-y-8">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Manage Success Stories</h1>
            {error && <p className="p-4 text-center font-semibold text-red-500 bg-red-100 dark:bg-red-900/50 rounded-lg">{error}</p>}
            {message && <p className="p-4 text-center font-semibold text-green-500 bg-green-100 dark:bg-green-900/50 rounded-lg">{message}</p>}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1">
                    <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex items-center gap-3">
                                <PlusCircleIcon className="w-8 h-8 text-orange-500" />
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Add New Story</h2>
                            </div>
                            <FormInput id="title" name="title" label="Title / Headline" type="text" value={formData.title} onChange={handleChange} />
                            <FormInput id="recipientName" name="recipientName" label="Recipient Name" type="text" value={formData.recipientName} onChange={handleChange} />
                            <TextArea id="storyText" name="storyText" label="Impact Message / Story" value={formData.storyText} onChange={handleChange} />
                            <FileInput id="image" name="image" label="Story Image" onChange={handleChange} />
                            <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50">
                                {isSubmitting ? 'Publishing...' : 'Publish Story'}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Published Stories</h2>
                        <div className="space-y-4">
                            {stories.length > 0 ? stories.map(story => (
                                <div key={story.id} className="flex items-center justify-between p-2 border-b dark:border-gray-600 last:border-b-0">
                                    <div className="flex items-center gap-4">
                                        <img src={story.image_url} alt={story.title} className="w-16 h-16 object-cover rounded-md" />
                                        <div>
                                            <p className="font-bold text-gray-800 dark:text-white">{story.title}</p>
                                            <p className="text-sm text-gray-500">Recipient: {story.recipient_name}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => handleDelete(story.id)} className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full" title="Delete Story">
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            )) : <p className="text-center text-gray-500 py-8">No success stories have been published yet.</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}